import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { DayPicker, type DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import { t } from 'utilities/TranslationUtils';
import { DEFAULT_TIMEFRAME, Timeframe, TimeframePreset, timeframeEnd, timeframeStart } from './playerPerformanceUtils';
import './TimeframePicker.scss';

const PRESETS: ReadonlyArray<{ preset: TimeframePreset; labelKey: string }> = [
  { preset: 'week', labelKey: 'playerPerformance.timeframeWeek' },
  { preset: 'month', labelKey: 'playerPerformance.timeframeMonth' },
  { preset: 'quarter', labelKey: 'playerPerformance.timeframeQuarter' },
  { preset: 'year', labelKey: 'playerPerformance.timeframeYear' },
  { preset: 'all', labelKey: 'playerPerformance.timeframeAll' },
];

interface TimeframePickerProps {
  timeframe: Timeframe;
  onChange: (timeframe: Timeframe) => void;
  /** Presets that have data; the others are hidden (the active one stays so the selection never vanishes). */
  availablePresets?: ReadonlySet<TimeframePreset>;
}

const MIN_MONTH = new Date(2018, 0);
const ISO_DAY = 'YYYY-MM-DD';

function rangeLabel(start: Date, end: Date | null): string {
  return end
    ? t('playerPerformance.timeframeRange', { from: start.toLocaleDateString(), to: end.toLocaleDateString() })
    : t('playerPerformance.timeframeFromDate', { date: start.toLocaleDateString() });
}

/** Timeframe selector: rolling presets, a date-range calendar popover, and a reset. */
const TimeframePicker: React.FunctionComponent<TimeframePickerProps> = ({ timeframe, onChange, availablePresets }) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const today = useMemo(() => new Date(), []);
  const rootRef = useRef<HTMLDivElement>(null);
  const isCustom = timeframe.preset === 'custom';
  const start = useMemo(() => timeframeStart(timeframe), [timeframe]);
  const end = useMemo(() => timeframeEnd(timeframe), [timeframe]);
  const selectedRange: DateRange | undefined = isCustom && start ? { from: start, to: end ?? undefined } : undefined;

  // Two-click range selection, handled here rather than by the library's range mode (whose first click already
  // reports a complete range). Click 1 anchors the start and applies an open-ended range (start → now); click 2
  // sets the end (either order), applies it and closes. Clicking the same day twice gives a single-day range.
  const [anchor, setAnchor] = useState<Date | null>(null);
  const handleDayClick = (day: Date) => {
    if (!anchor) {
      setAnchor(day);
      onChange({ preset: 'custom', from: dayjs(day).format(ISO_DAY), to: null });
      return;
    }
    const [first, last] = day < anchor ? [day, anchor] : [anchor, day];
    apply({ preset: 'custom', from: dayjs(first).format(ISO_DAY), to: dayjs(last).format(ISO_DAY) });
  };

  const apply = (next: Timeframe) => {
    onChange(next);
    setAnchor(null);
    setCalendarOpen(false);
  };

  useEffect(() => { if (!calendarOpen) setAnchor(null); }, [calendarOpen]);

  // Close the calendar on outside click / Escape.
  useEffect(() => {
    if (!calendarOpen) return undefined;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setCalendarOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setCalendarOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [calendarOpen]);

  return (
    <div className='pp-timeframe' ref={rootRef} data-test='pp-timeframe'>
      <div className='pp-timeframe__presets' role='group' aria-label={t('playerPerformance.timeframe')}>
        {PRESETS.filter(({ preset }) => !availablePresets || availablePresets.has(preset) || timeframe.preset === preset).map(({ preset, labelKey }) => (
          <button
            key={preset}
            type='button'
            className={`pp-timeframe__pill${timeframe.preset === preset ? ' is-active' : ''}`}
            aria-pressed={timeframe.preset === preset}
            onClick={() => apply({ preset, from: null })}
            data-test={`pp-timeframe-${preset}`}
          >
            {t(labelKey)}
          </button>
        ))}
      </div>

      <div className='pp-timeframe__custom'>
        {/* With a custom range active the date pill carries its own × to reset to all time. */}
        <span className={isCustom ? 'pp-timeframe__chip' : undefined}>
          <button
            type='button'
            className={`pp-timeframe__pill pp-timeframe__from${isCustom ? ' is-active' : ''}`}
            aria-expanded={calendarOpen}
            onClick={() => setCalendarOpen((open) => !open)}
            data-test='pp-timeframe-from'
          >
            <svg width='14' height='14' viewBox='0 0 14 14' fill='none' aria-hidden='true'>
              <rect x='1.5' y='2.5' width='11' height='10' rx='2' stroke='currentColor' strokeWidth='1.4' />
              <path d='M1.5 6h11M4.5 1v3M9.5 1v3' stroke='currentColor' strokeWidth='1.4' strokeLinecap='round' />
            </svg>
            {isCustom && start ? rangeLabel(start, end) : t('playerPerformance.timeframeFrom')}
          </button>
          {isCustom && (
            <button
              type='button'
              className='pp-timeframe__clear'
              aria-label={t('playerPerformance.timeframeReset')}
              title={t('playerPerformance.timeframeReset')}
              onClick={() => apply(DEFAULT_TIMEFRAME)}
              data-test='pp-timeframe-reset'
            >
              ×
            </button>
          )}
        </span>
        {calendarOpen && (
          <div className='pp-timeframe__popover' data-test='pp-timeframe-calendar'>
            <DayPicker
              mode='range'
              selected={anchor ? { from: anchor, to: anchor } : selectedRange}
              defaultMonth={start ?? today}
              disabled={{ after: today }}
              captionLayout='dropdown'
              startMonth={MIN_MONTH}
              endMonth={today}
              onDayClick={handleDayClick}
            />
          </div>
        )}
      </div>

    </div>
  );
};

export default TimeframePicker;
